import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { BarChart, Search } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import type { FiltroMaisVendidos } from "../../../services/listarProdutos"
import { useListarProdutosContext } from "../context/useListarProdutosContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    filtrosGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    filtrosGridBottom: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    botaoPesquisar: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        boxShadow: "0 4px 12px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
    },
    botaoExportar: {
        borderRadius: 25,
        marginLeft: theme.spacing(2),
        [theme.breakpoints.down("sm")]: { marginLeft: 0, marginTop: theme.spacing(1) },
    },
    acoes: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    semResultados: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(4),
    },
    infoQuantidade: {
        color: "#7f8c8d",
        marginBottom: theme.spacing(1),
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
}))

function hoje(): string {
    return new Date().toISOString().slice(0, 10)
}

function primeiroDiaMes(): string {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`
}

export function ComponentePrincipal() {
    const classes = useStyles()
    const { dados, erroCarregamento, pesquisar } = useListarProdutosContext()
    const { enqueueSnackbar } = useSnackbar()

    const [dataInicio, setDataInicio] = useState(primeiroDiaMes())
    const [dataFim, setDataFim] = useState(hoje())
    const [tipo, setTipo] = useState<1 | 2>(1)
    const [itemPesquisa, setItemPesquisa] = useState("")
    const [idCategoria, setIdCategoria] = useState<number | null>(null)

    const alertStart = (msg: string, variant: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const handlePesquisar = async () => {
        if (!dataInicio || !dataFim) {
            alertStart("Preencha as datas de início e fim.", "error")
            return
        }
        if (dataInicio > dataFim) {
            alertStart("A data de início não pode ser maior que a data fim.", "error")
            return
        }
        const filtro: FiltroMaisVendidos = {
            dataInicio,
            dataFim,
            tipo,
            itemPesquisa: itemPesquisa || undefined,
            idCategoria: idCategoria ?? undefined,
        }
        await pesquisar(filtro)
    }

    const handleExportar = () => {
        const cabecalho = tipo === 1
            ? ["Produto", "Quant. Vendida", "Valor Vendido"]
            : ["Adicional", "Quant. Vendida", "Valor Vendido"]

        const linhas = [
            cabecalho.join(";"),
            ...dados.pontos.map(p => [p.nome, String(p.valor), p.valorTotal].join(";")),
        ]
        const csv = linhas.join("\n")
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", tipo === 1 ? "produtos.csv" : "adicionais.csv")
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const titulo = tipo === 1 ? "Produtos mais vendidos" : "Adicionais mais vendidos"

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <BarChart className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Filtros de Pesquisa
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filtrosGrid}>
                            <TextField
                                label="Data Início"
                                type="date"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={dataInicio}
                                onChange={e => setDataInicio(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                label="Data Fim"
                                type="date"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={dataFim}
                                onChange={e => setDataFim(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                select
                                label="Tipo"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={tipo}
                                onChange={e => setTipo(Number(e.target.value) as 1 | 2)}
                            >
                                <MenuItem value={1}>Produto</MenuItem>
                                <MenuItem value={2}>Adicional</MenuItem>
                            </TextField>
                        </Box>
                        <Box className={classes.filtrosGridBottom}>
                            <TextField
                                label="Nome do item"
                                variant="outlined"
                                size="small"
                                fullWidth
                                placeholder="Filtrar por nome"
                                value={itemPesquisa}
                                onChange={e => setItemPesquisa(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <Search style={{ color: "#7f8c8d", fontSize: 20, marginRight: 4 }} />
                                    ),
                                }}
                            />
                            <TextField
                                select
                                label="Categoria"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={idCategoria ?? ""}
                                onChange={e =>
                                    setIdCategoria(e.target.value === "" ? null : Number(e.target.value))
                                }
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {dados.categorias.map(cat => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.nome}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        <Button
                            variant="contained"
                            className={classes.botaoPesquisar}
                            onClick={handlePesquisar}
                        >
                            Pesquisar
                        </Button>
                    </Box>
                </Paper>

                {dados.pesquisado && (
                    <Paper className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <BarChart className={classes.iconHeader} />
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                {titulo}
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Box className={classes.acoes}>
                                <Typography variant="body2" className={classes.infoQuantidade}>
                                    Exibindo {dados.pontos.length}{" "}
                                    {tipo === 1 ? "produto(s)" : "adicional(is)"}
                                </Typography>
                                {dados.pontos.length > 0 && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        className={classes.botaoExportar}
                                        onClick={handleExportar}
                                    >
                                        Exportar CSV
                                    </Button>
                                )}
                            </Box>
                            {dados.pontos.length === 0 ? (
                                <Typography className={classes.semResultados}>
                                    Não existem resultados para esta consulta.
                                </Typography>
                            ) : (
                                <TableContainer style={{ maxHeight: 500, overflowX: "auto" }}>
                                    <Table
                                        size="small"
                                        stickyHeader
                                        style={{ width: "100%", minWidth: 440 }}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ minWidth: 220 }}>
                                                    {tipo === 1 ? "Produto" : "Adicional"}
                                                </TableCell>
                                                <TableCell style={{ minWidth: 110 }} align="right">
                                                    Quant. Vendida
                                                </TableCell>
                                                <TableCell style={{ minWidth: 110 }} align="right">
                                                    Valor Vendido
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dados.pontos.map((ponto, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell
                                                        className={classes.nomeCell}
                                                        title={ponto.nome}
                                                    >
                                                        {ponto.nome}
                                                    </TableCell>
                                                    <TableCell align="right">{ponto.valor}</TableCell>
                                                    <TableCell align="right">{ponto.valorTotal}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Paper>
                )}
            </Box>
        </Box>
    )
}
